import React, { useCallback, useRef, useState } from 'react';
import { TurnstileInstance } from '@marsidev/react-turnstile';

import { supabase } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { TextInput } from 'components/ui/TextInput';
import { TurnstileWrapper } from 'components/ui/TurnstileWrapper';
import { Button } from 'components/ui/Button';

import { GrandLogoVert } from 'components/svg/GrandLogoVert';

import { colors, Fonts, transparencies } from 'styles/theme';
import { Bumper } from 'styles/shared';
import { AuthForm, AuthWrapper, StepsCompleteTextWrapper } from 'styles/auth';

export const PasswordReset = () => {
    const { showErrorToast } = useToast();

    const [email, setEmail] = useState<string | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [hasSubmittedRequest, setHasSubmittedRequest] =
        useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isSubmittingRequest, setIsSubmittingRequest] =
        useState<boolean>(false);

    const turnstileRef = useRef<TurnstileInstance>();

    const onEmailInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setEmail(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmittingRequest) {
                return;
            }
            setIsSubmittingRequest(true);

            if (captchaToken != null) {
                const { error } = await supabase.auth.resetPasswordForEmail(
                    email ?? '',
                    { captchaToken }
                );

                if (error == null) {
                    setHasSubmittedRequest(true);
                } else {
                    turnstileRef.current?.reset();
                    setIsError(true);
                    showErrorToast(error.message);
                }
            } else {
                showErrorToast(
                    'CAPTCHA verification is required to reset password'
                );
            }

            setIsSubmittingRequest(false);
        },
        [captchaToken, email, isSubmittingRequest, showErrorToast]
    );

    return (
        <AuthWrapper>
            <Bumper $shouldHideOnDesktop />
            <GrandLogoVert width={140} />
            <Bumper />
            {!hasSubmittedRequest ? (
                <AuthForm onSubmit={onSubmit}>
                    <Fonts.Heading6
                        $color={colors.mountainAsh}
                    >{`Reset password`}</Fonts.Heading6>
                    <Bumper />
                    <Fonts.Medium $textAlign="center">
                        {`Please enter your email to reset your password or get a new verification code`}
                    </Fonts.Medium>
                    <Bumper />
                    <TextInput
                        type="email"
                        id="email"
                        name="email"
                        onInput={onEmailInput}
                        label="Email address"
                        $isError={isError}
                        required
                        $backgroundColor={transparencies.darkJungle5}
                    />
                    <Bumper />
                    <TurnstileWrapper
                        turnstileRef={turnstileRef}
                        onSuccess={setCaptchaToken}
                    />
                    <Bumper />
                    <Button
                        type="submit"
                        value="Submit"
                        $isLoading={isSubmittingRequest}
                        $isStretch
                    >
                        {`Submit`}
                    </Button>
                </AuthForm>
            ) : (
                <StepsCompleteTextWrapper>
                    <Fonts.Heading6 $textAlign="center">{`Instructions sent to your email`}</Fonts.Heading6>
                    <Bumper />
                    <Fonts.Medium $textAlign="center">
                        {`Please check your email for reset instructions. If you need additional support please email hi@thegrand.world`}
                    </Fonts.Medium>
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                    <Bumper />
                </StepsCompleteTextWrapper>
            )}
        </AuthWrapper>
    );
};
